import React, { useEffect, useState } from 'react';
import { GitBranch, Users, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import axios from 'axios';

const TESTORA_BUG_STATUSES = [
  { key: 'open', label: 'Open' },
  { key: 'inprogress', label: 'In Progress' },
  { key: 'reopen', label: 'Reopen' },
  { key: 'resolve', label: 'Resolve / Closed' },
  { key: 'retest', label: 'Retest' },
];
const EMPTY_BUG_STATUS_MAPPING = Object.fromEntries(
  TESTORA_BUG_STATUSES.map(({ key }) => [key, '']),
);
const normalizeBugStatusMapping = (mapping) => Object.fromEntries(
  TESTORA_BUG_STATUSES.map(({ key }) => [
    key,
    typeof mapping?.[key] === 'string' ? mapping[key] : '',
  ]),
);

export default function IntegrationSettings({ darkMode }) {
  const cardBg = darkMode ? 'bg-[#161922]' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-800/50' : 'border-gray-200';
  const { selectedProject } = useSelector((state) => state.auth);
  const projectId = selectedProject?.project_id;
  const [jiraConnected, setJiraConnected] = useState(Boolean(selectedProject?.jira_connected));
  const [modalOpen, setModalOpen] = useState(false);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jiraForm, setJiraForm] = useState({ jira_base_url: '', jira_email: '', jira_api_token: '' });
  const [bugStatusMapping, setBugStatusMapping] = useState(EMPTY_BUG_STATUS_MAPPING);

  useEffect(() => {
    if (!projectId) {
      setJiraConnected(false);
      setBugStatusMapping(EMPTY_BUG_STATUS_MAPPING);
      return;
    }
    let cancelled = false;
    axios.get(`https://api.testoraglobal.in/api/project/id/${projectId}`)
      .then(({ data }) => data)
      .then((data) => {
        if (cancelled) return;
        const storedMapping = data.project?.bug_status_mapping;
        setJiraConnected(Boolean(data.project?.jira_connected));
        setBugStatusMapping(
          storedMapping && typeof storedMapping === 'object' && !Array.isArray(storedMapping)
            ? normalizeBugStatusMapping(storedMapping)
            : EMPTY_BUG_STATUS_MAPPING,
        );
      })
      .catch(() => {
        if (cancelled) return;
        setJiraConnected(Boolean(selectedProject?.jira_connected));
        setBugStatusMapping(EMPTY_BUG_STATUS_MAPPING);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, selectedProject?.jira_connected]);

  const saveJiraConfig = async (event) => {
    event.preventDefault();
    if (!projectId) {
      toast.error('Select a project before connecting Jira.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `https://api.testoraglobal.in/api/project/${projectId}/jira-config`,
        { ...jiraForm, bug_status_mapping: bugStatusMapping },
      );
      setJiraConnected(true);
      setModalOpen(false);
      setJiraForm({ jira_base_url: '', jira_email: '', jira_api_token: '' });
      toast.success('Jira connected for this project.');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBugStatusMapping = async (event) => {
    event.preventDefault();
    if (!projectId) {
      toast.error('Select a project before saving Jira status mappings.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `https://api.testoraglobal.in/api/project/${projectId}/jira-config`,
        { bug_status_mapping: bugStatusMapping },
      );
      setMappingModalOpen(false);
      toast.success('Jira bug status mapping saved.');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  const disconnectJira = async () => {
    if (!projectId) return toast.error('Select a project first.');
    try {
      await axios.put(
        `https://api.testoraglobal.in/api/project/${projectId}/jira-config`,
        { jira_base_url: '', jira_email: '', jira_api_token: '' },
      );
      setJiraConnected(false);
      setBugStatusMapping(EMPTY_BUG_STATUS_MAPPING);
      toast.success('Jira disconnected.');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const integrations = [
    { id: 'github', name: 'GitHub', desc: 'Connect with GitHub for CI/CD integration', icon: <GitBranch size={20} className="text-[#14b8a6]" />, connected: true },
    { id: 'jira', name: 'Jira', desc: 'Sync bugs and issues with Jira', icon: <div style={{color: '#14b8a6', fontSize: '20px'}}>J</div>, connected: jiraConnected },
    { id: 'teams', name: 'Microsoft Teams', desc: 'Send notifications to Teams channels', icon: <Users size={20} className="text-[#14b8a6]" />, connected: false }
  ];

  return (
    <div className={`p-10 rounded-3xl border ${cardBg} ${borderColor} animate-in fade-in slide-in-from-right-4 duration-300`}>
      <h3 className="text-lg font-bold mb-8">Integrations</h3>
      <div className="space-y-4">
        {integrations.map((item) => (
          <div key={item.id} className={`flex items-center justify-between p-6 rounded-2xl border ${borderColor} bg-[#0f111a]/50`}>
            <div className="flex items-center gap-5">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${borderColor} bg-gray-900`}>
                {item.icon}
              </div>
              <div>
                <p className="text-l font-bold text-[#f59e0b]">{item.name}</p>
                <p className="text-xs text-[#f59e0b] mt-1">{item.desc}</p>
              </div>
            </div>
            
            {item.id === 'jira' && item.connected ? (
              <div className="flex flex-wrap items-center justify-end gap-3">
                <span className="text-xs font-semibold text-green-500">Connected</span>
                <button onClick={() => setMappingModalOpen(true)} className={`px-4 py-2 rounded-lg border ${borderColor} text-xs font-bold hover:border-purple-500 hover:text-purple-500 transition-all`}>
                  Status Mapping
                </button>
                <button onClick={disconnectJira} className="px-4 py-2 rounded-lg border border-red-500/30 text-red-500 text-xs font-bold hover:bg-red-500/10 transition-all">
                  Disconnect
                </button>
              </div>
            ) : item.id === 'jira' ? (
              <button onClick={() => setModalOpen(true)} className="px-6 py-2 rounded-lg bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition-all shadow-lg shadow-purple-500/20">
                Connect
              </button>
            ) : item.connected ? (
              <button className="px-4 py-2 rounded-lg border border-red-500/30 text-red-500 text-xs font-bold hover:bg-red-500/10 transition-all">
                Disconnect
              </button>
            ) : (
              <button className="px-6 py-2 rounded-lg bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition-all shadow-lg shadow-purple-500/20">
                Connect
              </button>
            )}
          </div>
        ))}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={(event) => event.target === event.currentTarget && setModalOpen(false)}>
          <form onSubmit={saveJiraConfig} className={`w-full max-w-lg rounded-xl border ${borderColor} ${cardBg} p-6 shadow-2xl`}>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold">Connect Jira</h4>
                <p className="mt-1 text-sm text-gray-500">Credentials apply to {selectedProject?.project_name || 'the selected project'}.</p>
              </div>
              <button type="button" onClick={() => setModalOpen(false)} aria-label="Close dialog" className="rounded p-2 text-gray-500 hover:bg-gray-800/10"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {[
                ['jira_base_url', 'Jira Base URL', 'https://your-company.atlassian.net', 'url'],
                ['jira_email', 'Email', 'you@example.com', 'email'],
                ['jira_api_token', 'API Token', 'Paste your Jira API token', 'password'],
              ].map(([name, label, placeholder, type]) => (
                <label key={name} className="block text-sm font-medium">
                  {label}
                  <input required type={type} value={jiraForm[name]} placeholder={placeholder} onChange={(event) => setJiraForm((current) => ({ ...current, [name]: event.target.value }))} className={`mt-1.5 w-full rounded-lg border ${borderColor} bg-transparent px-3 py-2.5 outline-none focus:border-purple-500`} />
                </label>
              ))}
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setModalOpen(false)} className={`rounded-lg border ${borderColor} px-4 py-2 text-sm`}>Cancel</button>
              <button type="submit" disabled={saving} className="rounded-lg bg-purple-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Connecting...' : 'Connect Jira'}</button>
            </div>
          </form>
        </div>
      )}

      {mappingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={(event) => event.target === event.currentTarget && setMappingModalOpen(false)}>
          <form onSubmit={saveBugStatusMapping} className={`w-full max-w-lg rounded-xl border ${borderColor} ${cardBg} p-6 shadow-2xl`}>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold">Jira Bug Status Mapping</h4>
                <p className="mt-1 text-sm text-gray-500">
                  Match Testora bug statuses to the status names used by Jira.
                </p>
              </div>
              <button type="button" onClick={() => setMappingModalOpen(false)} aria-label="Close status mapping dialog" className="rounded p-2 text-gray-500 hover:bg-gray-800/10"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {TESTORA_BUG_STATUSES.map(({ key, label }) => (
                <label key={key} className="grid grid-cols-1 items-center gap-2 text-sm font-medium sm:grid-cols-[1fr_1.5fr]">
                  <span>{label}</span>
                  <input
                    type="text"
                    value={bugStatusMapping[key] || ''}
                    placeholder="Jira status name"
                    onChange={(event) => setBugStatusMapping((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))}
                    className={`w-full rounded-lg border ${borderColor} bg-transparent px-3 py-2.5 outline-none focus:border-purple-500`}
                  />
                </label>
              ))}
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setMappingModalOpen(false)} className={`rounded-lg border ${borderColor} px-4 py-2 text-sm`}>Cancel</button>
              <button type="submit" disabled={saving} className="rounded-lg bg-purple-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving...' : 'Save Mapping'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
